import { useAuth } from "@/contexts/AuthContext";
import {
  type InventoryOutlookSettings,
  type OutlookCadenceRecipient,
} from "@/lib/manufacturing/inventoryMatchClient";

const inputClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900";

export function InventoryOutlookCadence({
  settings,
  recipients,
  canManage,
  onSave,
  onSetRecipient,
}: {
  settings: InventoryOutlookSettings;
  recipients: OutlookCadenceRecipient[];
  canManage: boolean;
  onSave: (patch: Partial<InventoryOutlookSettings>) => void;
  onSetRecipient: (userId: string, optedIn: boolean) => void;
}) {
  const auth = useAuth();
  const selfId = auth.profile?.id ?? "";

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.opted_in}
            onChange={(e) => onSave({ opted_in: e.target.checked })}
          />
          Email me on the schedule
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.email_enabled}
            onChange={(e) => onSave({ email_enabled: e.target.checked })}
          />
          Org cadence on
        </label>
        <select
          className={inputClass}
          value={settings.cadence}
          aria-label="Org cadence"
          onChange={(e) => onSave({ cadence: e.target.value as InventoryOutlookSettings["cadence"] })}
        >
          <option value="weekly">Weekly</option>
          <option value="biweekly">Every 2 weeks</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>
      <div className="space-y-2">
        <p className="font-medium text-gray-800 dark:text-zinc-200">Who receives it</p>
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Checked people get the outlook on that cadence. You can change your own row. An admin can change anyone.
        </p>
        {recipients.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">No active people in this organization.</p>
        ) : (
          <ul className="max-h-64 space-y-1 overflow-y-auto">
            {recipients.map((person) => {
              const mine = person.user_id === selfId;
              const locked = !mine && !canManage;
              return (
                <li key={person.user_id}>
                  <label className={`flex items-center gap-2 ${locked ? "opacity-70" : ""}`}>
                    <input
                      type="checkbox"
                      checked={person.opted_in}
                      disabled={locked}
                      aria-label={`${person.name} receives the scheduled outlook`}
                      onChange={(e) => onSetRecipient(person.user_id, e.target.checked)}
                    />
                    <span>
                      {person.name}
                      {mine ? " (you)" : ""}
                      {person.email ? <span className="text-gray-500 dark:text-zinc-400"> · {person.email}</span> : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
